import {inject} from '@angular/core';

import {pipe, switchMap, tap} from 'rxjs';
import {patchState, signalStore, withMethods, withState} from '@ngrx/signals';
import {rxMethod} from '@ngrx/signals/rxjs-interop';
import {tapResponse} from '@ngrx/operators';
import {withDevtools} from '@angular-architects/ngrx-toolkit';

import {RequestStatus} from '@selpia/shared/data-access';
import {DataChunk} from '@selpia/shared/models';

import {ErrorMessage} from '@selpia/shared/error-handler';
import {DataBatchService} from '../data-services/data-batch.service';
import {DataBatchDto, LoadDataBatchFiltersDto} from '../dtos/load-data-batch.dto';
import {DTOsConverter} from '../util/dtos-converter';

export interface DataBatchState {
  dataChunks: DataChunk[] | undefined;
  summarizationKey: string | null;
  highlightedIds: number[];
  loadingStatus: RequestStatus;
}

const initialDataBatchState: DataBatchState = {
  dataChunks: undefined,
  summarizationKey: null,
  highlightedIds: [],
  loadingStatus: RequestStatus.Resolved,
};

export const DataBatchStore = signalStore(
  {providedIn: 'root'},
  withDevtools('data-batch'),
  withState(initialDataBatchState),
  withMethods((store, dataBatchService = inject(DataBatchService)) => ({
    loadDataBatch: rxMethod<[string, LoadDataBatchFiltersDto]>(
      pipe(
        tap(() =>
          patchState(store, {
            loadingStatus: RequestStatus.Pending,
          }),
        ),
        switchMap(([dimensionLevelName, filters]) => {
          return dataBatchService.loadDataBatch(dimensionLevelName, filters)
            .pipe(
              tapResponse({
                next: (dataBatch: DataBatchDto) => {
                  patchState(store, {
                    dataChunks: DTOsConverter.dtoDataBatch(dataBatch.dataChunks),
                    summarizationKey: dataBatch.summarizationKey,
                    loadingStatus: RequestStatus.Resolved,
                  });
                },
                error: (error: ErrorMessage) => {
                  patchState(store, {
                    loadingStatus: RequestStatus.Rejected,
                  });
                  throw error;
                },
              }),
            );
        }),
      ),
    ),
    clearDataBatch(): void {
      patchState(store, {
        dataChunks: undefined,
        summarizationKey: null,
      });
    },
    loadHighlights: rxMethod<[string, string]>(
      pipe(
        switchMap(([dataBatchHash, colouringStrategy]) => {
          return dataBatchService.loadHighlights(dataBatchHash, colouringStrategy)
            .pipe(
              tapResponse({
                next: (highlightedIds: number[]) => {
                  patchState(store, {
                    highlightedIds,
                  });
                },
                error: (error) => {
                  patchState(store, {
                    highlightedIds: [],
                  });
                  throw error;
                },
              }),
            );
        }),
      ),
    ),
  })),
);
export type DataBatchStore = InstanceType<typeof DataBatchStore>;
